import { Controller } from 'stimulus';
import gsap from 'gsap';
import prefersReducedMotion from '../helpers/prefersReducedMotion';
import dataLayer from '../tracking/dataLayer';

class LayoutController extends Controller {
  connect() {
    this.isNavOpen = false;
    this.isNavAnimating = false;

    this.isHeaderVisible = false;
    this.wasHeaderVisible = false;

    this.areHeaderElementsVisible = false;
    this.wereHeaderElementsVisible = false;

    this.lastScrollY = 0;
    this.directionDelta = 0;
    this.lastScrollDirection = null;

    this.headerOffset = this.calculateHeaderOffset();

    this.hideNavigationComponents();

    this.headerElements = [
      this.headerLogoTarget,
    ];

    // On normal pages we auto-show the header...
    if (document.body.className.indexOf('movement-header') < 0) {
      this.isHeaderVisible = true;
      this.areHeaderElementsVisible = true;
      this.toggleHeader();
    }

    gsap.to(document.body, {
      opacity: 1,
      duration: 1,
      ease: 'power1.out',
    });
  }

  onWindowScroll() {
    const top = window.scrollY;

    const direction = top < this.lastScrollY ? 'up' : 'down';

    // We keep track of how much the user has scrolled in a particular direction
    // when the scroll direction changes, we reset this count.
    if (direction !== this.lastScrollDirection) {
      this.directionDelta = 0;
    }

    // Add the number of scrolled pixels between this and the last tick
    // to the delta.
    this.directionDelta += Math.abs(top - this.lastScrollY);

    // When we've scrolled past a particular point we want to make the
    // logo slightly smaller to fit in the header.
    this.headerTarget.classList[top > 100 ? 'add' : 'remove']('is-small-logo');

    // Some pages (like home) have a class that forces the interface
    // like the header from being shown on page load.
    const isHideInterface = document.body.classList.contains('hide-interface');

    // Check if the user is scrolling up.
    const isScrollUp = (top >= 100 && direction === 'up' && this.directionDelta > 300);

    const navigationIsOpen = document.getElementById('navigation').getAttribute('aria-expanded') === 'true';

    // Check if we match all criteria for showing the header.
    this.isHeaderVisible = ((top < 100 || isScrollUp) && !isHideInterface) || navigationIsOpen;

    // Reflect header visibility state to elements in DOM...
    this.toggleHeader();

    this.lastScrollDirection = direction;
    this.lastScrollY = top;
  }

  onWindowResize() {
    this.headerOffset = this.calculateHeaderOffset();
  }

  hideNavigationComponents() {
    gsap.set([this.headerTarget].concat(this.headerElements), {
      opacity: 0,
    });
  }

  onNavToggle() {
    if (this.isNavAnimating) {
      return;
    }

    this.isNavOpen = !this.isNavOpen;
    this.isNavAnimating = true;

    this.areHeaderElementsVisible = !this.isNavOpen;

    dataLayer.event('navigation', this.isNavOpen ? 'open' : 'close');

    if (this.isNavOpen) {
      gsap.set(this.navigationItemTargets, {
        opacity: 0,
      });
    }

    setTimeout(() => {
      this.isNavAnimating = false;
    }, 200);

    this.toggleHeader();
    this.toggleNavigationBackground();
    this.toggleNavigationA11y();
    this.toggleNavigation();
  }

  toggleNavigation() {
    gsap.killTweensOf([this.navigationTarget, this.navigationItemTargets]);

    gsap.to(this.navigationTarget, {
      opacity: this.isNavOpen ? 1 : 0,
      visibility: this.isNavOpen ? 'visible' : 'hidden',
      delay: this.isNavOpen ? 0.5 : 0,
      duration: this.isNavOpen ? 2 : 0.5,
      ease: 'power3.out',
    });

    this.navigationTarget.style.pointerEvents = this.isNavOpen ? 'auto' : 'none';

    return gsap.fromTo(this.navigationItemTargets, {
      x: !this.isNavOpen ? 0 : 20,
      opacity: !this.isNavOpen ? 1 : 0,
    }, {
      x: this.isNavOpen ? 0 : 20,
      opacity: this.isNavOpen ? 1 : 0,
      duration: this.isNavOpen ? 2 : 0.5,
      delay: this.isNavOpen ? 0.75 : 0,
      stagger: this.isNavOpen ? 0.25 : 0,
      ease: 'power3.out',
    });
  }

  toggleNavigationA11y() {
    this.headerNavigationToggleTarget.setAttribute('aria-expanded', this.isNavOpen ? 'true' : 'false');
    this.navigationTarget.setAttribute('aria-expanded', this.isNavOpen ? 'true' : 'false');
  }

  toggleHeader() {
    if (this.isHeaderVisible !== this.wasHeaderVisible) {
      gsap.killTweensOf(this.headerTarget);

      gsap.fromTo(this.headerTarget, {
        opacity: this.isHeaderVisible ? 0 : 1,
      }, {
        autoAlpha: this.isHeaderVisible ? 1 : 0,
        y: this.isHeaderVisible ? 0 : -20,
        ease: 'power2.out',
        duration: prefersReducedMotion() ? 0 : 1.5,
      });

      this.wasHeaderVisible = this.isHeaderVisible;
    }

    if (this.areHeaderElementsVisible !== this.wereHeaderElementsVisible) {
      gsap.killTweensOf(this.headerElements);

      gsap.to(this.headerElements, {
        y: this.areHeaderElementsVisible ? 0 : -10,
        autoAlpha: this.areHeaderElementsVisible ? 1 : 0,
        stagger: prefersReducedMotion() ? 0 : 0.5,
        duration: prefersReducedMotion() ? 0 : 1.5,
        ease: 'power2.out',
      });

      this.wereHeaderElementsVisible = this.areHeaderElementsVisible;
    }

    // Toggle header--dark class on header to change its content to a dark color...
    this.headerTarget.classList[this.isNavOpen ? 'add' : 'remove']('header--dark');
  }

  toggleNavigationBackground() {
    const {
      offsetLeft,
      offsetTop,
      offsetWidth,
    } = this.navigationBackgroundTarget;

    const destinationSize = Math.sqrt((window.innerWidth ** 2) + (window.innerHeight ** 2));

    gsap.killTweensOf(this.navigationBackgroundTarget);

    return gsap.to(this.navigationBackgroundTarget, {
      scale: this.isNavOpen ? destinationSize / offsetWidth : offsetWidth,
      x: this.isNavOpen ? ((window.innerWidth / 2) - offsetLeft - (offsetWidth / 2)) : 0,
      y: this.isNavOpen ? ((window.innerHeight / 2) - offsetTop - (offsetWidth / 2)) : 0,
      autoAlpha: this.isNavOpen ? 1 : 0,
      duration: prefersReducedMotion() ? 0 : 1,
      ease: 'power3.out',
    });
  }

  calculateHeaderOffset() {
    const windowHeaderOffset = this.headerSpacerTarget.getBoundingClientRect().top;
    const windowScroll = window.scrollY;

    return windowHeaderOffset + windowScroll;
  }
}

LayoutController.targets = [
  'header',
  'headerLogo',
  'headerNavigationToggle',
  'headerSpacer',
  'navigationBackground',
  'navigation',
  'navigationItem',
];

export default LayoutController;
